import { Close } from "@mui/icons-material";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FormEvent, useCallback, useState } from "react";
import { useUpdateBannerMutation } from "src/api/services/bannersApi";
import { PromoBanner } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { desktopSx } from "src/utils/styles/modalStyles";

type Props = {
  open: boolean;
  onClose: () => void;
  banner: PromoBanner | null;
};

const UpdateBannerModal = ({ open, onClose, banner }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bannerText, setBannerText] = useState<string>(banner?.text ?? "");
  const [error, setError] = useState<string | null>(null);
  const [updateBanner] = useUpdateBannerMutation();

  const handleUpdate = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (banner?.id == null) return;

      setLoading(true);
      updateBanner({
        id: banner.id,
        body: {
          text: bannerText,
        },
      })
        .unwrap()
        .catch(() => {
          setError("Something went wrong. Please try again later.");
        })
        .finally(() => {
          onClose();
          setLoading(false);
        });
    },
    [banner?.id, bannerText, onClose, updateBanner]
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Update Banner
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack
          gap={2}
          component="form"
          autoComplete="off"
          onSubmit={handleUpdate}
        >
          <FormControl>
            <TextField
              multiline
              minRows={3}
              label="Banner text"
              value={bannerText}
              onChange={(e) => setBannerText(e.target.value)}
              required
            />
          </FormControl>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton
            disabled={bannerText.length === 0}
            loading={loading}
            type="submit"
          >
            Update
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdateBannerModal;
